import React from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  FileText,
  Briefcase,
  MessageSquare,
  Award,
  Building2,
  Settings,
  BarChart,
  HelpCircle,
  Mail,
  Image,
  Code,
  Globe,
  GitBranch,
  ScrollText,
  LogOut,
  Ticket,
  CreditCard,
  User
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import Overview from '../admin/Overview';
import BlogManager from '../admin/BlogManager';
import WorkManager from '../admin/WorkManager';
import CareerManager from '../admin/CareerManager';
import TestimonialManager from '../admin/TestimonialManager';
import CertificationManager from '../admin/CertificationManager';
import IndustryManager from '../admin/IndustryManager';
import MetricsManager from '../admin/MetricsManager';
import FAQManager from '../admin/FAQManager';
import ContactSubmissionsManager from '../admin/ContactSubmissionsManager';
import ClientLogosManager from '../admin/ClientLogosManager';
import TechnologyStackManager from '../admin/TechnologyStackManager';
import ServicesManager from '../admin/ServicesManager';
import ProcessStepsManager from '../admin/ProcessStepsManager';
import LogViewer from '../admin/LogViewer';
import SupportTickets from './SupportTickets';
import SubscriptionManagement from './SubscriptionManagement';
import ClientProfile from './ClientProfile';

export default function CyBieLayout() {
  const location = useLocation();
  const { user, signOut, loading } = useAuthStore();

  console.log('CyBieLayout Render - Loading:', loading, 'User:', user?.email, 'Role:', user?.role);

  // If still loading auth, show loading state
  if (loading) {
    console.log('Showing loading screen...');
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading CyBie Portal...</p>
          <p className="text-xs text-gray-400 mt-2">Auth loading state: {loading ? 'true' : 'false'}</p>
        </div>
      </div>
    );
  }

  // If no user, redirect to login
  if (!user) {
    console.log('No user found, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  console.log('CyBie Portal - User authenticated:', user?.email, 'Role:', user?.role);

  const adminNavigation = [
    { name: 'Overview', href: '/cybie', icon: LayoutDashboard, roles: ['admin'] },
    { name: 'Blog Posts', href: '/cybie/blogs', icon: FileText, roles: ['admin'] },
    { name: 'Work Portfolio', href: '/cybie/work', icon: Briefcase, roles: ['admin'] },
    { name: 'Careers', href: '/cybie/careers', icon: Users, roles: ['admin'] },
    { name: 'Testimonials', href: '/cybie/testimonials', icon: MessageSquare, roles: ['admin'] },
    { name: 'Certifications', href: '/cybie/certifications', icon: Award, roles: ['admin'] },
    { name: 'Industries', href: '/cybie/industries', icon: Building2, roles: ['admin'] },
    { name: 'Services', href: '/cybie/services', icon: Globe, roles: ['admin'] },
    { name: 'Process Steps', href: '/cybie/process-steps', icon: GitBranch, roles: ['admin'] },
    { name: 'FAQs', href: '/cybie/faqs', icon: HelpCircle, roles: ['admin'] },
    { name: 'Contact Forms', href: '/cybie/contact-submissions', icon: Mail, roles: ['admin'] },
    { name: 'Client Logos', href: '/cybie/client-logos', icon: Image, roles: ['admin'] },
    { name: 'Tech Stack', href: '/cybie/technologies', icon: Code, roles: ['admin'] },
    { name: 'Website Metrics', href: '/cybie/metrics', icon: BarChart, roles: ['admin'] },
    { name: 'System Logs', href: '/cybie/logs', icon: ScrollText, roles: ['admin'] },
  ];

  const clientNavigation = [
    { name: 'My Profile', href: '/cybie/profile', icon: User, roles: ['client', 'admin'] },
    { name: 'Support Tickets', href: '/cybie/tickets', icon: Ticket, roles: ['client', 'admin'] },
    { name: 'Subscription', href: '/cybie/subscription', icon: CreditCard, roles: ['client', 'admin'] },
  ];

  const externalPortals = [
    {
      name: 'Edge Media Hub',
      abbr: 'EMH',
      href: '/emh',
      icon: MessageSquare,
      description: 'Social Media Management',
      roles: ['admin', 'emh']
    }
  ];

  const filteredAdminNav = adminNavigation.filter(item =>
    item.roles.includes(user?.role || '')
  );

  const filteredClientNav = clientNavigation.filter(item =>
    item.roles.includes(user?.role || '')
  );

  const filteredPortals = externalPortals.filter(portal =>
    portal.roles.includes(user?.role || '')
  );

  // Debug: Log filtered navigation
  console.log('Filtered Admin Nav:', filteredAdminNav.length, 'items');
  console.log('Filtered Client Nav:', filteredClientNav.length, 'items');
  console.log('Filtered Portals:', filteredPortals.length, 'items');

  const handleSignOut = async () => {
    await signOut();
    window.location.href = '/login';
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Top Navigation Bar */}
      <header className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                  <LayoutDashboard className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold">CyBie</h1>
                  <p className="text-xs text-blue-100">Content Management & Client Portal</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <div className="font-medium">{user?.email}</div>
                <div className="text-xs text-blue-100 capitalize">{user?.role}</div>
              </div>
              <button
                onClick={handleSignOut}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
          <nav className="p-4">
            {/* Admin Section */}
            {filteredAdminNav.length > 0 && (
              <div className="mb-6">
                <h2 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Website Management
                </h2>
                <div className="space-y-1">
                  {filteredAdminNav.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                          isActive
                            ? 'bg-blue-50 text-blue-600'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        <item.icon className={`mr-3 h-5 w-5 ${isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-500'}`} />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Client Section */}
            {filteredClientNav.length > 0 && (
              <div className="mb-6">
                <h2 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Client Portal
                </h2>
                <div className="space-y-1">
                  {filteredClientNav.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                          isActive
                            ? 'bg-blue-50 text-blue-600'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        <item.icon className={`mr-3 h-5 w-5 ${isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-500'}`} />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* External Portals */}
            {filteredPortals.length > 0 && (
              <div className="mb-6">
                <h2 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  External Portals
                </h2>
                <div className="space-y-1">
                  {filteredPortals.map((portal) => (
                    <Link
                      key={portal.name}
                      to={portal.href}
                      className="group flex items-center px-3 py-2 text-sm font-medium rounded-lg text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    >
                      <div className="mr-3 h-8 w-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-white text-xs font-bold">
                        {portal.abbr}
                      </div>
                      <div>
                        <div>{portal.name}</div>
                        <div className="text-xs text-gray-500">{portal.description}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Settings */}
            <div className="border-t border-gray-200 pt-4">
              <Link
                to="/cybie/settings"
                className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  location.pathname === '/cybie/settings'
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <Settings className={`mr-3 h-5 w-5 ${location.pathname === '/cybie/settings' ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-500'}`} />
                Settings
              </Link>
            </div>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <Routes>
            <Route index element={user?.role === 'admin' ? <Overview /> : <Navigate to="/cybie/profile" />} />

            {/* Admin Routes */}
            <Route path="blogs/*" element={<BlogManager />} />
            <Route path="work/*" element={<WorkManager />} />
            <Route path="careers/*" element={<CareerManager />} />
            <Route path="testimonials/*" element={<TestimonialManager />} />
            <Route path="certifications/*" element={<CertificationManager />} />
            <Route path="industries/*" element={<IndustryManager />} />
            <Route path="services/*" element={<ServicesManager />} />
            <Route path="process-steps/*" element={<ProcessStepsManager />} />
            <Route path="faqs/*" element={<FAQManager />} />
            <Route path="contact-submissions/*" element={<ContactSubmissionsManager />} />
            <Route path="client-logos/*" element={<ClientLogosManager />} />
            <Route path="technologies/*" element={<TechnologyStackManager />} />
            <Route path="metrics/*" element={<MetricsManager />} />
            <Route path="logs" element={<LogViewer />} />

            {/* Client Routes */}
            <Route path="profile" element={<ClientProfile />} />
            <Route path="tickets" element={<SupportTickets />} />
            <Route path="subscription" element={<SubscriptionManagement />} />

            {/* Settings Route */}
            <Route path="settings" element={<div className="p-8"><h1 className="text-2xl font-bold">Settings</h1></div>} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
